/* eslint-disable @typescript-eslint/no-explicit-any */
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { Toast } from 'sharedUi/Toast';
import PageSkeleton from '../../components/common/page-skeleton';
import ScreenHeader from '../../components/common/screen-header';
import { Message } from 'primereact/message';
import { InputNumber } from 'primereact/inputnumber';
import { Button } from 'sharedUi/Button';
import { Input } from 'sharedUi/Input';
import { TextArea } from 'sharedUi/TextArea';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import { useAccessibleProjects, projectFieldOptions, appIdFromField, defaultProjectId, SHARED_PROJECT } from '../../hooks/useProjectScope';
import FormSelect from '../../components/forms/form-select';
import { functionsService } from '../../services/functions.service';
import { valueSetsService } from '../../services/value-sets.service';
import { extractErrorMessage } from '../../utils/error-handler';

const schema = z.object({
  functionCode: z.string().regex(/^[A-Z0-9_]{2,64}$/, 'Uppercase letters, digits, underscore (2–64)'),
  capability: z.string().min(1, 'Capability is required'),
  functionType: z.string().min(1, 'Type is required'),
  description: z.string().max(1000).optional().or(z.literal('')),
  // Empty means shared: the API reads a blank appId as the global bucket.
  // Required: a blank appId is what the API reads as "shared with every
  // project", so an unset field would quietly author a global row.
  appId: z.string().min(1, 'Choose the project this belongs to.'),
  handlerKey: z.string().optional().or(z.literal('')),
  // IMPORT_PATH
  modulePath: z.string().optional().or(z.literal('')),
  callable: z.string().optional().or(z.literal('')),
  parameterMapping: z.string().optional().or(z.literal('')),  // JSON object
  // QUERY
  collection: z.string().optional().or(z.literal('')),
  filterTemplate: z.string().optional().or(z.literal('')),
  projection: z.string().optional().or(z.literal('')),
  // API_CALL
  connectionRef: z.string().optional().or(z.literal('')),
  path: z.string().optional().or(z.literal('')),
  method: z.string().optional().or(z.literal('')),
  bodyTemplate: z.string().optional().or(z.literal('')),
  // EXPRESSION
  expression: z.string().optional().or(z.literal('')),
  inputDefinition: z.string().optional().or(z.literal('')),  // JSON array
  outputDefinition: z.string().optional().or(z.literal('')), // JSON array
  defaultTimeoutSeconds: z.number().nullable().optional(),
});
type FormData = z.infer<typeof schema>;

const _vsOpts = (vs: any) => (vs?.items || []).map((i: any) => ({ value: i.code, label: i.label }));
const _json = (v: unknown): string => { try { return v ? JSON.stringify(v, null, 2) : ''; } catch { return ''; } };
const _parseArr = (s?: string): any[] => { if (!s?.trim()) return []; try { const p = JSON.parse(s); return Array.isArray(p) ? p : []; } catch { return []; } };
const _parseObj = (s?: string): any => { if (!s?.trim()) return {}; try { const p = JSON.parse(s); return typeof p === 'object' && p ? p : {}; } catch { return {}; } };

const FunctionForm = () => {
  const { id, isReady, navigateToList } = useSecureNavigation({ entity: 'function', basePath: '/functions' });
  const isEdit = Boolean(id);
  // function_master is the one master whose API honours a body appId (when it
  // is in the caller's scope), so this is a real choice, not a display.
  const { projects, canScopeByProject } = useAccessibleProjects();
  const queryClient = useQueryClient();
  const toast = useRef<Toast>(null);

  const { control, handleSubmit, reset, watch, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { functionCode: '', appId: defaultProjectId(projects), capability: 'EVAL_TARGET', functionType: 'REGISTERED_HANDLER', description: '', handlerKey: '', modulePath: '', callable: '', parameterMapping: '', collection: '', filterTemplate: '', projection: '', connectionRef: '', path: '', method: 'GET', bodyTemplate: '', expression: '', inputDefinition: '', outputDefinition: '', defaultTimeoutSeconds: 10 },
    mode: 'onChange',
  });
  const functionType = watch('functionType');

  // Handlers are an allowlist in code, so the form can only offer them if the
  // registry is readable. When it is not, the field stays typeable rather than
  // becoming a picker with nothing in it.
  const { data: handlerKeys } = useQuery({
    queryKey: ['functionHandlers'],
    queryFn: () => functionsService.handlers(),
    staleTime: 3e5,
  });
  const handlerOptions = (handlerKeys || []).map((k: string) => ({ value: k, label: k }));

  const { data: typeVS } = useQuery({ queryKey: ['vs', 'functionType'], queryFn: () => valueSetsService.getByKey('functionType', 'en'), staleTime: 3e5 });
  const { data: capVS } = useQuery({ queryKey: ['vs', 'functionCapability'], queryFn: () => valueSetsService.getByKey('functionCapability', 'en'), staleTime: 3e5 });

  const { data: fnData, isLoading } = useQuery({ queryKey: ['function', id], queryFn: () => functionsService.getById(id!), enabled: isEdit && isReady && !!id });

  useEffect(() => {
    if (fnData && isEdit) {
      reset({
        appId: fnData.appId || SHARED_PROJECT,
        functionCode: fnData.functionCode || '',
        capability: fnData.capability || 'EVAL_TARGET',
        functionType: fnData.functionType || 'REGISTERED_HANDLER',
        description: fnData.description || '',
        handlerKey: (fnData.configuration as any)?.handlerKey || '',
        modulePath: (fnData.configuration as any)?.modulePath || '',
        callable: (fnData.configuration as any)?.callable || '',
        parameterMapping: _json((fnData.configuration as any)?.parameterMapping),
        collection: (fnData.configuration as any)?.collection || '',
        filterTemplate: _json((fnData.configuration as any)?.filterTemplate),
        projection: _json((fnData.configuration as any)?.projection),
        connectionRef: (fnData.configuration as any)?.connectionRef || '',
        path: (fnData.configuration as any)?.path || '',
        method: (fnData.configuration as any)?.method || 'GET',
        bodyTemplate: _json((fnData.configuration as any)?.bodyTemplate),
        expression: (fnData.configuration as any)?.expression || '',
        inputDefinition: _json(fnData.inputDefinition), outputDefinition: _json(fnData.outputDefinition),
        defaultTimeoutSeconds: fnData.defaultTimeoutSeconds ?? 10,
      });
    }
  }, [fnData, isEdit, reset]);

  const mutation = useMutation({
    mutationFn: (data: any) => (isEdit && id ? functionsService.update(id, data) : functionsService.create(data)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['functions'] });
      toast.current?.show({ severity: 'success', summary: 'Success', detail: `Function ${isEdit ? 'updated' : 'created'}`, life: 3000 });
      setTimeout(() => navigateToList(), 800);
    },
    onError: (e: any) => toast.current?.show({ severity: 'error', summary: 'Error', detail: extractErrorMessage(e, 'Save failed'), life: 10000 }),
  });

  const onSubmit = (d: FormData) => {
    // Only the keys this type uses. Carrying the others over would leave a
    // QUERY function holding an API path nothing reads — configuration that
    // looks like it does something.
    const configuration =
      d.functionType === 'REGISTERED_HANDLER' ? { handlerKey: d.handlerKey || '' }
      : d.functionType === 'IMPORT_PATH' ? {
          modulePath: d.modulePath || '',
          callable: d.callable || '',
          ...(d.parameterMapping?.trim() ? { parameterMapping: _parseObj(d.parameterMapping) } : {}),
        }
      : d.functionType === 'QUERY' ? {
          collection: d.collection || '',
          filterTemplate: _parseObj(d.filterTemplate),
          // Omitted rather than empty: the resolver builds a projection from
          // the declared outputs when there is none, and {} would select nothing.
          ...(d.projection?.trim() ? { projection: _parseObj(d.projection) } : {}),
        }
      : d.functionType === 'API_CALL' ? {
          connectionRef: d.connectionRef || '',
          path: d.path || '',
          method: d.method || 'GET',
          ...(d.bodyTemplate?.trim() ? { bodyTemplate: _parseObj(d.bodyTemplate) } : {}),
        }
      : d.functionType === 'EXPRESSION' ? { expression: d.expression || '' }
      : {};
    mutation.mutate({
      functionCode: d.functionCode, capability: d.capability, functionType: d.functionType,
      // Create only — FunctionUpdateRequest omits appId, so a project cannot be
      // changed after the fact and sending one would be silently dropped.
      ...(isEdit ? {} : { appId: appIdFromField(d.appId) }),
      description: d.description || null, configuration,
      inputDefinition: _parseArr(d.inputDefinition), outputDefinition: _parseArr(d.outputDefinition),
      defaultTimeoutSeconds: d.defaultTimeoutSeconds ?? 10,
    });
  };

  if (isEdit && isLoading) return <PageSkeleton />;

  return (
    <div className="p-4" data-testid="SCR-Function-Form">
      <Toast ref={toast} position="top-right" />
      <ScreenHeader icon="pi pi-code" title={isEdit ? 'Edit Function' : 'Create Function'} onBack={() => navigateToList()} />
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4 w-full">
        <div className="ds-card p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Controller name="functionCode" control={control} render={({ field }) => (
            <Input label="Function code *" {...field} error={errors.functionCode?.message} placeholder="SUPPORT_TICKET_AGENT" disabled={isEdit} />
          )} />
          {canScopeByProject && (
            <Controller name="appId" control={control} render={({ field }) => (
              <FormSelect label="Project" name={field.name} value={field.value || ''}
                options={projectFieldOptions(projects, field.value)}
                onChange={field.onChange}
                helperText={isEdit
                  ? 'Set when the function was created; the API does not move one afterwards.'
                  : 'Which project owns this. It cannot be moved afterwards.'}
                disabled={isEdit} />
            )} />
          )}
          <Controller name="capability" control={control} render={({ field }) => (
            <FormSelect label="Capability *" name={field.name} value={field.value || ''} options={_vsOpts(capVS)} onChange={field.onChange} error={!!errors.capability} helperText={errors.capability?.message} />
          )} />
          <Controller name="functionType" control={control} render={({ field }) => (
            <FormSelect label="Type *" name={field.name} value={field.value || ''} options={_vsOpts(typeVS)} onChange={field.onChange} error={!!errors.functionType} helperText={errors.functionType?.message} />
          )} />
          <Controller name="defaultTimeoutSeconds" control={control} render={({ field }) => (
            <div className="flex flex-col gap-1"><label className="text-sm font-medium text-gray-700 dark:text-gray-300">Timeout (s)</label>
              <InputNumber value={field.value ?? 10} onValueChange={(e) => field.onChange(e.value ?? 10)} min={1} max={600} className="w-full" /></div>
          )} />
          {functionType === 'REGISTERED_HANDLER' && (
            <Controller name="handlerKey" control={control} render={({ field }) => (
              handlerOptions.length > 0 ? (
                <FormSelect
                  label="Handler *" name={field.name} value={field.value || ''}
                  options={handlerOptions} onChange={field.onChange}
                  helperText="The registered code this resolves through."
                />
              ) : (
                // No registry to offer, so the field stays typeable rather than
                // becoming a picker with nothing in it.
                <div className="flex flex-col gap-1">
                  <Input label="Handler key *" {...field} placeholder="get_users_by_role" />
                  <small className="text-xs text-gray-400">Checked against the allowlist on save.</small>
                </div>
              )
            )} />
          )}

          {functionType === 'IMPORT_PATH' && (
            <>
              <Controller name="modulePath" control={control} render={({ field }) => (
                <Input label="Module path *" {...field}
                       placeholder="app.plugins.agents.support_agent" />
              )} />
              <Controller name="callable" control={control} render={({ field }) => (
                <Input label="Callable *" {...field} placeholder="answer" />
              )} />
              <Controller name="parameterMapping" control={control} render={({ field }) => (
                <div className="sm:col-span-2">
                  <TextArea label="Parameter mapping (JSON)" {...field} rows={3}
                    placeholder='{ "question": "{{input}}" }' />
                  <small className="text-xs text-gray-400">
                    Optional. Maps the callable&apos;s params from the case input for an eval
                    target (<code>{'{{input}}'}</code> / <code>{'{{input.field}}'}</code>). For a
                    recipient chain the inputs come from the attachment&apos;s inputMapping, so
                    this can be left empty. The path is imported and run as-is — keep
                    FUNCTION_MASTER write access to trusted admins.
                  </small>
                </div>
              )} />
            </>
          )}

          {functionType === 'QUERY' && (
            <>
              <Controller name="collection" control={control} render={({ field }) => (
                <div className="flex flex-col gap-1">
                  <Input label="Collection *" {...field} placeholder="personnel_master" />
                  <small className="text-xs text-gray-400">Read directly, so it must be a collection this app owns.</small>
                </div>
              )} />
              <Controller name="filterTemplate" control={control} render={({ field }) => (
                <div className="sm:col-span-2">
                  <TextArea label="Filter template (JSON)" {...field} rows={3}
                    placeholder='{ "unitId": "{{unitId}}", "isActive": true }' />
                  <small className="text-xs text-gray-400">
                    <code>{'{{name}}'}</code> is substituted from this step&apos;s inputs. A value
                    that is exactly one placeholder keeps its type — an id stays an id, which
                    a rendered string would not.
                  </small>
                </div>
              )} />
              <Controller name="projection" control={control} render={({ field }) => (
                <div className="sm:col-span-2">
                  <TextArea label="Projection (JSON)" {...field} rows={2} placeholder='{ "userId": 1 }' />
                  <small className="text-xs text-gray-400">
                    Optional — left empty, the declared outputs decide which fields are read.
                  </small>
                </div>
              )} />
            </>
          )}

          {functionType === 'API_CALL' && (
            <>
              <Controller name="connectionRef" control={control} render={({ field }) => (
                <div className="flex flex-col gap-1">
                  <Input label="Connection" {...field} placeholder="CRM_API" />
                  <small className="text-xs text-gray-400">A stored connection supplies the base URL and its credentials, which never enter this row.</small>
                </div>
              )} />
              <Controller name="method" control={control} render={({ field }) => (
                <FormSelect label="Method" name={field.name} value={field.value || 'GET'}
                  options={['GET', 'POST', 'PUT', 'PATCH'].map((m) => ({ value: m, label: m }))}
                  onChange={field.onChange} />
              )} />
              <Controller name="path" control={control} render={({ field }) => (
                <div className="sm:col-span-2">
                  <Input label="Path *" {...field} placeholder="/api/v1/users/{{unitId}}" />
                </div>
              )} />
              <Controller name="bodyTemplate" control={control} render={({ field }) => (
                <div className="sm:col-span-2">
                  <TextArea label="Body template (JSON)" {...field} rows={3} placeholder='{ "unitId": "{{unitId}}" }' />
                </div>
              )} />
            </>
          )}

          {functionType === 'EXPRESSION' && (
            <div className="sm:col-span-2 flex flex-col gap-2">
              <Message severity="warn" className="w-full"
                text="An EXPRESSION function cannot be attached to a recipient chain — it is refused on save until the sandbox it needs exists." />
              <Controller name="expression" control={control} render={({ field }) => (
                <TextArea label="Expression" {...field} rows={2} />
              )} />
            </div>
          )}
          <Controller name="description" control={control} render={({ field }) => (
            <div className="sm:col-span-2"><TextArea label="Description" {...field} rows={2} /></div>
          )} />
        </div>

        <div className="ds-card p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <h2 className="sm:col-span-2 text-sm font-semibold text-ink">I/O contract (JSON arrays)</h2>
          <Controller name="inputDefinition" control={control} render={({ field }) => (
            <TextArea label="Input definition" {...field} rows={4} placeholder='[{ "name": "ticket", "dataType": "string", "isRequired": true }]' />
          )} />
          <Controller name="outputDefinition" control={control} render={({ field }) => (
            <TextArea label="Output definition" {...field} rows={4} placeholder='[{ "name": "answer", "dataType": "string" }]' />
          )} />
        </div>

        <div className="flex justify-end gap-2">
          <Button type="button" label="Cancel" severity="secondary" outlined onClick={() => navigateToList()} />
          <Button type="submit" label={mutation.isPending ? 'Saving…' : isEdit ? 'Update' : 'Create'} icon={mutation.isPending ? 'pi pi-spin pi-spinner' : 'pi pi-check'} disabled={mutation.isPending} />
        </div>
      </form>
    </div>
  );
};

export default FunctionForm;
